import { create } from "zustand";
import {
  clearWatchlist,
  createWatchlist,
  fetchSecMasterCusips,
  fetchWatchlistItems,
  fetchWatchlists,
  updateWatchlist,
} from "../http/api";
import {
  normalizeCusip,
  normalizeCusipList,
} from "../Utils/maInventoryMarketDataUtils";
import { mapWatchlistBondTypeToApiType } from "../Utils/maWatchlistFilterUtils";
import { MA_WATCHLIST_PERSISTED_CUSIPS_FETCH_SIZE } from "../Constants/AppConstants";
import {
  isBackendDynamicWatchlist,
  isWatchlistFailed,
  normalizeWatchlistStatus,
  WATCHLIST_STATUS,
} from "../Utils/maWatchlistStatusUtils";
import { DAILY_PROCESS_BLOCKING_STATUS } from "./maInventoryStore";

const DEFAULT_PAGINATION = {
  pageNumber: 0,
  pageSize: 7,
  totalElements: 0,
  totalPages: 0,
  hasNext: false,
  hasPrevious: false,
  isFirst: true,
  isLast: true,
};

const WATCHLIST_FETCH_SIZE = 100;
const WATCHLIST_ITEMS_FETCH_SIZE = MA_WATCHLIST_PERSISTED_CUSIPS_FETCH_SIZE;
const WATCHLIST_TYPE_STATIC = "static";
const WATCHLIST_TYPE_DYNAMIC = "dynamic";

const DEFAULT_SECMASTER_PAGINATION = {
  pageNumber: 0,
  pageSize: 7,
  totalElements: 0,
  totalPages: 0,
  hasNext: false,
  hasPrevious: false,
  isFirst: true,
  isLast: true,
};

const DEFAULT_DAILY_PROCESS_GATE = {
  status: "UNKNOWN",
  isBlocked: false,
  message: null,
};

const hasWatchlistCharacteristics = (characteristics) => {
  if (characteristics === null || characteristics === undefined) {
    return false;
  }

  if (typeof characteristics === "string") {
    return characteristics.trim() !== "";
  }

  if (Array.isArray(characteristics)) {
    return characteristics.length > 0;
  }

  if (typeof characteristics === "object") {
    return Object.keys(characteristics).length > 0;
  }

  return Boolean(characteristics);
};

const normalizeWatchlistType = (value) =>
  `${value || ""}`.toLowerCase() === WATCHLIST_TYPE_DYNAMIC
    ? WATCHLIST_TYPE_DYNAMIC
    : WATCHLIST_TYPE_STATIC;

const getDynamicWatchlistType = (dynamicCriteria = null) => {
  if (!dynamicCriteria || typeof dynamicCriteria !== "object") {
    return "";
  }

  return (
    dynamicCriteria.type
    || mapWatchlistBondTypeToApiType(dynamicCriteria.bondType)
  );
};

const buildDynamicCharacteristicsPayload = (dynamicCriteria = null) => {
  const characteristics =
    (dynamicCriteria?.filters && typeof dynamicCriteria.filters === "object"
      ? dynamicCriteria.filters
      : dynamicCriteria?.characteristics);
  if (!characteristics || typeof characteristics !== "object") {
    return null;
  }

  const dynamicType = getDynamicWatchlistType(dynamicCriteria);

  return {
    ...characteristics,
    ...(dynamicType && { type: dynamicType }),
  };
};

const normalizeWatchlist = (watchlist) => {
  const characteristics = watchlist?.characteristics ?? "";
  const inferredWatchlistType =
    watchlist?.watchlistType
      ? normalizeWatchlistType(watchlist.watchlistType)
      : hasWatchlistCharacteristics(characteristics)
      ? WATCHLIST_TYPE_DYNAMIC
      : WATCHLIST_TYPE_STATIC;

  return {
    watchlistId: watchlist?.watchlistId ?? watchlist?.id ?? null,
    watchlistName: watchlist?.watchlistName ?? "",
    watchlistType: inferredWatchlistType,
    bondType: watchlist?.bondType ?? watchlist?.type ?? "",
    characteristics,
    status: normalizeWatchlistStatus(watchlist?.status),
  };
};

const normalizeSecMasterBond = (bond = {}) => {
  const securityDesc = `${bond?.securityDesc ?? bond?.description ?? ""}`.trim();

  return {
    cusip: normalizeCusip(bond?.cusip),
    bondType: bond?.bondType ?? bond?.type ?? bond?.securityType ?? "",
    securityDesc,
    // Keep description as a legacy alias for existing consumers.
    description: securityDesc,
    partOfWatchlist: Number(bond?.partOfWatchlist) === 1,
  };
};

const buildPaginationState = (payload = {}, fallbackPageSize = 7) => ({
  pageNumber: payload?.pageNumber ?? 0,
  pageSize: payload?.pageSize ?? fallbackPageSize,
  totalElements: payload?.totalElements ?? 0,
  totalPages: payload?.totalPages ?? 0,
  hasNext: Boolean(payload?.hasNext),
  hasPrevious: Boolean(payload?.hasPrevious),
  isFirst: payload?.isFirst ?? true,
  isLast: payload?.isLast ?? true,
});

const getPagePayload = (response = {}) => {
  if (Array.isArray(response?.data?.content)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data?.content)) {
    return response.data.data;
  }

  if (Array.isArray(response?.content)) {
    return response;
  }

  return response?.data || {};
};

const fetchWatchlistCusipPage = async (
  token,
  {
    page = 0,
    size = WATCHLIST_ITEMS_FETCH_SIZE,
    sortBy = "cusip",
    sortDirection = "DESC",
    searchString = "",
    watchlistId = null,
    criteria = null,
  } = {}
) => {
  const response = await fetchWatchlistItems(
    token,
    page,
    size,
    sortBy,
    sortDirection,
    searchString,
    watchlistId,
    criteria
  );
  const payload = getPagePayload(response);
  const pageContent = Array.isArray(payload?.content) ? payload.content : [];

  const normalizedWatchlistItems = pageContent.map(normalizeSecMasterBond);

  return {
    cusips: normalizeCusipList(
      normalizedWatchlistItems.map((item) => item.cusip)
    ),
    secMasterByCusip: createBondMap(normalizedWatchlistItems),
    pagination: buildPaginationState(payload, size),
  };
};

const fetchAllWatchlistCusips = async (
  token,
  {
    size = MA_WATCHLIST_PERSISTED_CUSIPS_FETCH_SIZE,
    sortBy = "cusip",
    sortDirection = "DESC",
    watchlistId = null,
  } = {}
) => {
  let page = 0;
  let hasNextPage = true;
  let allCusips = [];
  let secMasterByCusip = {};

  while (hasNextPage) {
    const watchlistItemsState = await fetchWatchlistCusipPage(token, {
      page,
      size,
      sortBy,
      sortDirection,
      searchString: "",
      watchlistId,
    });

    allCusips = [...allCusips, ...watchlistItemsState.cusips];
    secMasterByCusip = {
      ...secMasterByCusip,
      ...watchlistItemsState.secMasterByCusip,
    };

    const { pagination } = watchlistItemsState;
    const nextPageNumber = page + 1;
    hasNextPage = Boolean(
      pagination?.hasNext
      || (!pagination?.isLast && nextPageNumber < (pagination?.totalPages ?? 0))
    );
    page = nextPageNumber;
  }

  return {
    cusips: normalizeCusipList(allCusips),
    secMasterByCusip,
  };
};

const createBondMap = (bonds = []) => {
  const nextMap = {};

  bonds.forEach((bond) => {
    const normalizedBond = normalizeSecMasterBond(bond);
    if (normalizedBond.cusip) {
      nextMap[normalizedBond.cusip] = normalizedBond;
    }
  });

  return nextMap;
};

export const useMAWatchlistStore = create((set, get) => ({
  watchlists: [],
  currentWatchlist: null,
  watchlistCusips: [],
  secMasterBonds: [],
  secMasterByCusip: {},
  loading: false,
  secMasterLoading: false,
  saving: false,
  error: null,
  secMasterError: null,
  watchlistItemsError: null,
  watchlistsPagination: { ...DEFAULT_PAGINATION },
  itemsPagination: { ...DEFAULT_PAGINATION },
  secMasterPagination: { ...DEFAULT_SECMASTER_PAGINATION },
  dailyProcessGate: { ...DEFAULT_DAILY_PROCESS_GATE },
  setCurrentWatchlist: (watchlist) => set({ currentWatchlist: watchlist }),
  upsertWatchlist: (watchlist) => {
    const normalizedWatchlist = normalizeWatchlist(watchlist);
    const canUpsertWatchlist =
      normalizedWatchlist?.watchlistId !== null
      && normalizedWatchlist?.watchlistId !== undefined
      && normalizedWatchlist?.watchlistId !== "";

    set((state) => {
      if (!canUpsertWatchlist) {
        return state;
      }

      const nextWatchlists = [
        normalizedWatchlist,
        ...state.watchlists.filter(
          (currentWatchlist) =>
            currentWatchlist.watchlistId !== normalizedWatchlist.watchlistId
        ),
      ];
      const isCurrentWatchlist =
        String(state.currentWatchlist?.watchlistId ?? "")
          === String(normalizedWatchlist.watchlistId);

      return {
        watchlists: nextWatchlists,
        currentWatchlist: isCurrentWatchlist
          ? normalizedWatchlist
          : state.currentWatchlist,
      };
    });

    return normalizedWatchlist;
  },
  clearLoadedWatchlistPages: () =>
    set({
      watchlistCusips: [],
      secMasterBonds: [],
      loading: false,
      secMasterLoading: false,
      secMasterError: null,
      watchlistItemsError: null,
      itemsPagination: { ...DEFAULT_PAGINATION },
      secMasterPagination: { ...DEFAULT_SECMASTER_PAGINATION },
    }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),
  clearSecMasterError: () => set({ secMasterError: null }),
  clearWatchlistItemsError: () => set({ watchlistItemsError: null }),
  setDailyProcessGate: (gate = {}) =>
    set((state) => ({
      dailyProcessGate: {
        ...state.dailyProcessGate,
        ...gate,
      },
    })),
  resetDailyProcessGate: () =>
    set({
      dailyProcessGate: { ...DEFAULT_DAILY_PROCESS_GATE },
    }),
  reset: () =>
    set({
      watchlists: [],
      currentWatchlist: null,
      watchlistCusips: [],
      secMasterBonds: [],
      secMasterByCusip: {},
      loading: false,
      secMasterLoading: false,
      saving: false,
      error: null,
      secMasterError: null,
      watchlistItemsError: null,
      watchlistsPagination: { ...DEFAULT_PAGINATION },
      itemsPagination: { ...DEFAULT_PAGINATION },
      secMasterPagination: { ...DEFAULT_SECMASTER_PAGINATION },
      dailyProcessGate: { ...DEFAULT_DAILY_PROCESS_GATE },
    }),
  loadSecMasterPage: async (
    token,
    {
      page = 0,
      size = 7,
      sortBy = "cusip",
      sortDirection = "DESC",
      searchString = "",
      watchlistId = null,
      criteria = null,
    } = {}
  ) => {
    if (!token) {
      set({
        secMasterBonds: [],
        secMasterPagination: { ...DEFAULT_SECMASTER_PAGINATION },
        secMasterLoading: false,
      });
      return [];
    }

    set({ secMasterLoading: true, secMasterError: null });

    try {
      const resolvedWatchlistId =
        watchlistId ?? get().currentWatchlist?.watchlistId ?? null;

      const response = await fetchSecMasterCusips(
        token,
        page,
        size,
        sortBy,
        sortDirection,
        searchString,
        resolvedWatchlistId,
        criteria
      );
      const payload = getPagePayload(response);
      const secMasterBonds = Array.isArray(payload?.content)
        ? payload.content.map(normalizeSecMasterBond)
        : [];

      set((state) => ({
        secMasterBonds,
        secMasterLoading: false,
        secMasterPagination: buildPaginationState(payload, size),
        secMasterByCusip: {
          ...state.secMasterByCusip,
          ...createBondMap(secMasterBonds),
        },
      }));

      return secMasterBonds;
    } catch (error) {
      const message = error?.message || "Failed to load sec master bonds";
      set({ secMasterLoading: false, secMasterError: message });
      throw error;
    }
  },
  hydrateCusipDetails: async (_token, cusips = []) => {
    const normalizedCusips = normalizeCusipList(cusips);
    const { secMasterByCusip } = get();

    const missingCusips = normalizedCusips.filter((cusip) => !secMasterByCusip[cusip]);
    if (!missingCusips.length) {
      return secMasterByCusip;
    }

    const detailMap = createBondMap(
      missingCusips.map((cusip) => ({
        cusip,
        securityDesc: "",
        description: "",
      }))
    );
    set((state) => ({
      secMasterByCusip: {
        ...state.secMasterByCusip,
        ...detailMap,
      },
    }));

    return {
      ...get().secMasterByCusip,
      ...detailMap,
    };
  },
  loadWatchlistData: async (
    token,
    {
      watchlistItemsPage = 0,
      watchlistItemsPageSize = WATCHLIST_ITEMS_FETCH_SIZE,
      watchlistItemsSortBy = "cusip",
      watchlistItemsSortDirection = "DESC",
      watchlistItemsSearchString = "",
      initialWatchlistId = null,
      criteria = null,
      skipWatchlistItems = false,
    } = {}
  ) => {
    if (!token) {
      set({
        watchlists: [],
        currentWatchlist: null,
        watchlistCusips: [],
        secMasterBonds: [],
        secMasterByCusip: {},
        watchlistsPagination: { ...DEFAULT_PAGINATION },
        itemsPagination: { ...DEFAULT_PAGINATION },
        secMasterPagination: { ...DEFAULT_SECMASTER_PAGINATION },
        error: null,
        watchlistItemsError: null,
      });
      return {
        watchlists: [],
        currentWatchlist: null,
        watchlistCusips: [],
      };
    }

    set({ loading: true, error: null, watchlistItemsError: null });

    try {
      const watchlistsResponse = await fetchWatchlists(
        token,
        0,
        WATCHLIST_FETCH_SIZE,
        "id",
        "DESC"
      );
      const watchlistsPayload = watchlistsResponse?.data || {};
      const watchlists = Array.isArray(watchlistsPayload?.content)
        ? watchlistsPayload.content.map(normalizeWatchlist)
        : [];
      const preferredWatchlist = initialWatchlistId
        ? watchlists.find(
          (watchlist) => String(watchlist?.watchlistId) === String(initialWatchlistId)
        )
        : null;
      const currentWatchlist = preferredWatchlist || watchlists[0] || null;

      let watchlistItemsState = {
        cusips: [],
        pagination: { ...DEFAULT_PAGINATION },
      };

      if (!skipWatchlistItems && currentWatchlist?.watchlistId) {
        watchlistItemsState = await fetchWatchlistCusipPage(
          token,
          {
            page: watchlistItemsPage,
            size: watchlistItemsPageSize,
            sortBy: watchlistItemsSortBy,
            sortDirection: watchlistItemsSortDirection,
            searchString: watchlistItemsSearchString,
            watchlistId: currentWatchlist.watchlistId,
            criteria,
          }
        );
      }

      const nextState = {
        watchlists,
        currentWatchlist,
        watchlistCusips: watchlistItemsState.cusips,
        loading: false,
        error: null,
        watchlistItemsError: null,
        watchlistsPagination: buildPaginationState(
          watchlistsPayload,
          WATCHLIST_FETCH_SIZE
        ),
        itemsPagination: watchlistItemsState.pagination,
      };

      if (watchlistItemsState.secMasterByCusip) {
        nextState.secMasterByCusip = {
          ...get().secMasterByCusip,
          ...watchlistItemsState.secMasterByCusip,
        };
      }

      set(nextState);

      return {
        watchlists,
        currentWatchlist,
        watchlistCusips: watchlistItemsState.cusips,
        itemsPagination: watchlistItemsState.pagination,
      };
    } catch (error) {
      const message = error?.message || "Failed to load watchlist data";
      set({ loading: false, error: message });
      throw error;
    }
  },
  loadWatchlistItemsPage: async (
    token,
    {
      watchlistItemsPage = 0,
      watchlistItemsPageSize = WATCHLIST_ITEMS_FETCH_SIZE,
      watchlistItemsSortBy = "cusip",
      watchlistItemsSortDirection = "DESC",
      watchlistItemsSearchString = "",
      watchlistItemsWatchlistId = null,
      criteria = null,
    } = {}
  ) => {
    if (!token) {
      set({
        watchlistCusips: [],
        itemsPagination: { ...DEFAULT_PAGINATION },
        watchlistItemsError: null,
      });

      return {
        watchlistCusips: [],
      };
    }

    set({ loading: true, watchlistItemsError: null });

    try {
      const resolvedWatchlistId =
        watchlistItemsWatchlistId ?? get().currentWatchlist?.watchlistId ?? null;

      const watchlistItemsState = await fetchWatchlistCusipPage(token, {
        page: watchlistItemsPage,
        size: watchlistItemsPageSize,
        sortBy: watchlistItemsSortBy,
        sortDirection: watchlistItemsSortDirection,
        searchString: watchlistItemsSearchString,
        watchlistId: resolvedWatchlistId,
        criteria,
      });

      const nextState = {
        watchlistCusips: watchlistItemsState.cusips,
        itemsPagination: watchlistItemsState.pagination,
        loading: false,
        watchlistItemsError: null,
      };

      if (watchlistItemsState.secMasterByCusip) {
        nextState.secMasterByCusip = {
          ...get().secMasterByCusip,
          ...watchlistItemsState.secMasterByCusip,
        };
      }

      set(nextState);

      return {
        watchlistCusips: watchlistItemsState.cusips,
        itemsPagination: watchlistItemsState.pagination,
      };
    } catch (error) {
      const message = error?.message || "Failed to load watchlist items";
      set({ loading: false, watchlistItemsError: message });
      throw error;
    }
  },
  loadPersistedWatchlistCusips: async (
    token,
    {
      size = MA_WATCHLIST_PERSISTED_CUSIPS_FETCH_SIZE,
      sortBy = "cusip",
      sortDirection = "DESC",
      watchlistId = null,
    } = {}
  ) => {
    if (!token) {
      return [];
    }

    try {
      const persistedState = await fetchAllWatchlistCusips(token, {
        size,
        sortBy,
        sortDirection,
        watchlistId: watchlistId ?? get().currentWatchlist?.watchlistId ?? null,
      });

      if (persistedState?.secMasterByCusip) {
        set((state) => ({
          secMasterByCusip: {
            ...state.secMasterByCusip,
            ...persistedState.secMasterByCusip,
          },
        }));
      }

      return persistedState.cusips;
    } catch (error) {
      const message = error?.message || "Failed to load persisted watchlist items";
      set({ watchlistItemsError: message });
      throw error;
    }
  },
  saveWatchlist: async (
    token,
    {
      watchlistName,
      nextCusips = [],
      baseCusips = null,
      watchlistType = "static",
      dynamicCriteria = null,
      switchedFromBackendDynamicToStatic = false,
    }
  ) => {
    if (!token) {
      throw new Error("Authentication token is required");
    }

    const { currentWatchlist, watchlistCusips, watchlists, dailyProcessGate } = get();
    if (
      dailyProcessGate?.isBlocked
      || dailyProcessGate?.status === DAILY_PROCESS_BLOCKING_STATUS
    ) {
      const message =
        dailyProcessGate?.message
        || "Daily watchlist update is currently running. Please try again later.";
      set({ error: message });
      throw new Error(message);
    }

    const trimmedWatchlistName = `${watchlistName || ""}`.trim();
    const effectiveWatchlistName =
      trimmedWatchlistName || `${currentWatchlist?.watchlistName}`.trim();

    if (!effectiveWatchlistName) {
      const message = "Watchlist name is required";
      set({ error: message });
      throw new Error(message);
    }

    const normalizedNextCusips = normalizeCusipList(nextCusips);

    const normalizedBaseCusips = Array.isArray(baseCusips)
      ? normalizeCusipList(baseCusips)
      : normalizeCusipList(watchlistCusips);
    const currentCusipSet = new Set(normalizedBaseCusips);
    const nextCusipSet = new Set(normalizedNextCusips);

    const addedCusips = normalizedNextCusips.filter((cusip) => !currentCusipSet.has(cusip));
    const removedCusips = Array.from(currentCusipSet).filter(
      (cusip) => !nextCusipSet.has(cusip)
    );
    const normalizedTargetWatchlistType = normalizeWatchlistType(watchlistType);
    const isDynamicWatchlist = normalizedTargetWatchlistType === WATCHLIST_TYPE_DYNAMIC;
    const wasBackendDynamicSwitchedToStatic =
      !isDynamicWatchlist && Boolean(switchedFromBackendDynamicToStatic);
    const submittedAddedCusips = isDynamicWatchlist
      ? []
      : wasBackendDynamicSwitchedToStatic
      ? normalizedNextCusips
      : addedCusips;
    const submittedRemovedCusips = isDynamicWatchlist
      ? []
      : wasBackendDynamicSwitchedToStatic
      ? []
      : removedCusips;

    const isNameUnchanged =
      `${currentWatchlist?.watchlistName || ""}`.trim() === effectiveWatchlistName;
    const hasExistingWatchlist =
      currentWatchlist?.watchlistId !== null
      && currentWatchlist?.watchlistId !== undefined
      && currentWatchlist?.watchlistId !== ""
      && Number.isFinite(Number(currentWatchlist.watchlistId));

    const currentWatchlistType = normalizeWatchlistType(currentWatchlist?.watchlistType);
    const hasWatchlistMetadataChange =
      currentWatchlistType !== normalizedTargetWatchlistType
      || (isDynamicWatchlist && Boolean(dynamicCriteria));

    if (
      hasExistingWatchlist
      && isNameUnchanged
      && !addedCusips.length
      && !removedCusips.length
      && !hasWatchlistMetadataChange
    ) {
      return {
        noop: true,
        watchlist: currentWatchlist,
        watchlistCusips: normalizedBaseCusips,
      };
    }

    const dynamicWatchlistType = getDynamicWatchlistType(dynamicCriteria);
    const dynamicCharacteristicsPayload = buildDynamicCharacteristicsPayload(dynamicCriteria);

    set({ saving: true, error: null });

    try {
      let response;

      if (hasExistingWatchlist) {
        response = await updateWatchlist(token, currentWatchlist.watchlistId, {
          watchlist: {
            watchlistName: effectiveWatchlistName,
            watchlistType: normalizedTargetWatchlistType,
            ...(dynamicCriteria?.bondType && { bondType: dynamicCriteria.bondType }),
            ...(dynamicWatchlistType && { type: dynamicWatchlistType }),
            ...(dynamicCharacteristicsPayload && {
              characteristics: JSON.stringify(dynamicCharacteristicsPayload),
            }),
          },
          addedCusips: submittedAddedCusips,
          removedCusips: submittedRemovedCusips,
          isRetry: 0,
        });
      } else {
        response = await createWatchlist(token, {
          watchlist: {
            watchlistName: effectiveWatchlistName,
            watchlistType: normalizedTargetWatchlistType,
            ...(dynamicCriteria?.bondType && { bondType: dynamicCriteria.bondType }),
            ...(dynamicWatchlistType && { type: dynamicWatchlistType }),
            ...(dynamicCharacteristicsPayload && {
              characteristics: JSON.stringify(dynamicCharacteristicsPayload),
            }),
          },
          addedCusips: isDynamicWatchlist ? [] : normalizedNextCusips,
          ...(isDynamicWatchlist && { removedCusips: [] }),
          isRetry: 0,
        });
      }

      const responseStatus = normalizeWatchlistStatus(
        response?.status ?? response?.watchlist?.status
      );
      const resolvedWatchlistPayload = response?.watchlist
        ? {
          ...response.watchlist,
          status: responseStatus,
        }
        : currentWatchlist;
      const normalizedWatchlist = resolvedWatchlistPayload
        ? normalizeWatchlist(resolvedWatchlistPayload)
        : null;
      const canUpsertWatchlist =
        normalizedWatchlist?.watchlistId !== null
        && normalizedWatchlist?.watchlistId !== undefined
        && normalizedWatchlist?.watchlistId !== "";
      const nextWatchlists = canUpsertWatchlist
        ? [
          normalizedWatchlist,
          ...watchlists.filter(
            (watchlist) => watchlist.watchlistId !== normalizedWatchlist.watchlistId
          ),
        ]
        : watchlists;
      const nextState = {
        currentWatchlist: normalizedWatchlist,
        watchlists: nextWatchlists,
        saving: false,
        error: null,
      };

      if (responseStatus === WATCHLIST_STATUS.DONE) {
        nextState.watchlistCusips = normalizedNextCusips;
      }

      set(nextState);

      if (responseStatus === WATCHLIST_STATUS.DONE) {
        await get().hydrateCusipDetails(token, normalizedNextCusips);
      }

      return {
        ...response,
        status: responseStatus,
        watchlist: normalizedWatchlist,
        watchlistCusips:
          responseStatus === WATCHLIST_STATUS.DONE
            ? normalizedNextCusips
            : normalizedBaseCusips,
      };
    } catch (error) {
      const message = error?.message || "Failed to save watchlist";
      set({ saving: false, error: message });
      throw error;
    }
  },
  retryFailedDynamicWatchlist: async (token) => {
    if (!token) {
      throw new Error("Authentication token is required");
    }

    const { currentWatchlist, watchlists } = get();
    const watchlistId = currentWatchlist?.watchlistId;

    if (!watchlistId) {
      throw new Error("No watchlist selected to retry");
    }

    if (!isBackendDynamicWatchlist(currentWatchlist) || !isWatchlistFailed(currentWatchlist)) {
      throw new Error("Retry is only available for failed dynamic watchlists");
    }

    set({ saving: true, error: null });

    try {
      const response = await updateWatchlist(token, watchlistId, {
        watchlist: {
          watchlistName: `${currentWatchlist?.watchlistName ?? ""}`.trim(),
          watchlistType: WATCHLIST_TYPE_DYNAMIC,
          characteristics: "",
        },
        addedCusips: [],
        removedCusips: [],
        isRetry: 1,
      });

      const responseStatus = normalizeWatchlistStatus(
        response?.status ?? response?.watchlist?.status
      );
      const normalizedWatchlist = normalizeWatchlist({
        ...currentWatchlist,
        ...(response?.watchlist || {}),
        status: responseStatus,
        watchlistType:
          response?.watchlist?.watchlistType ?? currentWatchlist?.watchlistType,
        characteristics: response?.watchlist?.characteristics ?? "",
      });
      const nextWatchlists = [
        normalizedWatchlist,
        ...watchlists.filter(
          (watchlist) => watchlist.watchlistId !== normalizedWatchlist.watchlistId
        ),
      ];

      set({
        currentWatchlist: normalizedWatchlist,
        watchlists: nextWatchlists,
        saving: false,
        error: null,
      });

      return {
        ...response,
        status: responseStatus,
        watchlist: normalizedWatchlist,
      };
    } catch (error) {
      const message = error?.message || "Failed to retry watchlist";
      set({ saving: false, error: message });
      throw error;
    }
  },
  clearCurrentWatchlist: async (token) => {
    if (!token) {
      throw new Error("Authentication token is required");
    }

    const { currentWatchlist, dailyProcessGate } = get();
    if (
      dailyProcessGate?.isBlocked
      || dailyProcessGate?.status === DAILY_PROCESS_BLOCKING_STATUS
    ) {
      const message =
        dailyProcessGate?.message
        || "Daily watchlist update is currently running. Please try again later.";
      set({ error: message });
      throw new Error(message);
    }

    const watchlistId = currentWatchlist?.watchlistId;

    if (!watchlistId) {
      throw new Error("No watchlist selected to clear");
    }

    set({ saving: true, error: null });

    try {
      const response = await clearWatchlist(token, watchlistId);

      if (response?.success !== true) {
        throw new Error(response?.message || "Failed to clear watchlist");
      }

      set((state) => {
        const nextSecMasterByCusip = Object.keys(state.secMasterByCusip).reduce(
          (accumulator, cusip) => {
            accumulator[cusip] = {
              ...state.secMasterByCusip[cusip],
              partOfWatchlist: false,
            };
            return accumulator;
          },
          {}
        );

        const nextSecMasterBonds = state.secMasterBonds.map((bond) => ({
          ...bond,
          partOfWatchlist: false,
        }));

        return {
          watchlistCusips: [],
          secMasterByCusip: nextSecMasterByCusip,
          secMasterBonds: nextSecMasterBonds,
          itemsPagination: {
            ...state.itemsPagination,
            totalElements: 0,
            totalPages: 0,
            hasNext: false,
            hasPrevious: false,
            isFirst: true,
            isLast: true,
            pageNumber: 0,
          },
          saving: false,
          error: null,
        };
      });

      return response;
    } catch (error) {
      const message = error?.message || "Failed to clear watchlist";
      set({ saving: false, error: message });
      throw error;
    }
  },
}));
